import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, Factory, Store } from 'lucide-react';
import { DataTable, type Column } from '@/components/common/DataTable';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { productionService, jobWorkOrdersService } from '@/services/production';
import { warehousesService } from '@/services/inventory';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import type { JobWorkOrderRow, JobWorkOrderStatus } from '@/types/production';
import { JobWorkOrderDetailSheet } from './JobWorkOrderDetailSheet';

type StatusFilter = 'all' | JobWorkOrderStatus;

const STATUS_LABEL: Record<JobWorkOrderStatus, string> = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};
const STATUS_TONE: Record<JobWorkOrderStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  DRAFT: 'muted',
  APPROVED: 'warning',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  REJECTED: 'destructive',
  CANCELLED: 'muted',
};

export function JobWorkOrdersPage() {
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const query = useQuery({
    queryKey: ['production', 'job-work-orders', { status, search: debounced }],
    queryFn: () =>
      jobWorkOrdersService.list({
        status: status === 'all' ? undefined : status,
        search: debounced || undefined,
        pageSize: 100,
      }),
  });
  const rows = query.data?.items ?? [];

  const tabs: StatusTabOption[] = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'DRAFT', label: 'Draft' },
      { value: 'APPROVED', label: 'Approved' },
      { value: 'IN_PROGRESS', label: 'In progress' },
      { value: 'COMPLETED', label: 'Completed' },
    ],
    [],
  );

  const columns: Column<JobWorkOrderRow>[] = [
    { key: 'jwoNumber', header: 'JWO', cell: (r) => <span className="font-mono text-[11px]">{r.jwoNumber}</span> },
    {
      key: 'stepName',
      header: 'Step',
      cell: (r) => (
        <div className="flex items-center gap-1.5">
          {r.mode === 'OUTSOURCED' ? <Store className="h-3.5 w-3.5 text-muted-foreground" /> : <Factory className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className="text-foreground">{r.stepName}</span>
        </div>
      ),
    },
    {
      key: 'output',
      header: 'Produces',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate text-foreground">{r.outputItem.name}</div>
          <div className="text-[10.5px] text-muted-foreground tabular-nums">×{fmt.number(Number(r.plannedOutputQty))}</div>
        </div>
      ),
    },
    { key: 'where', header: 'Where', cell: (r) => <span className="text-[11px] text-muted-foreground">{r.vendor ? r.vendor.name : r.warehouse.name}</span> },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => <Badge variant={STATUS_TONE[r.status]} className="text-[10px]">{STATUS_LABEL[r.status]}</Badge>,
    },
    { key: 'createdAt', header: 'Created', cell: (r) => <span className="text-[11px] text-muted-foreground">{fmt.date(r.createdAt)}</span> },
  ];

  return (
    <div className="space-y-4">
      <ListPageHeader
        title="Job Work Orders"
        description="Run production steps against available stock — consume materials, produce the output. In-house or issued to a job-work contractor."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> New job work
          </Button>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <StatusTabs options={tabs} value={status} onChange={(v) => setStatus(v as StatusFilter)} />
        <div className="relative w-64 max-w-full">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search JWO / step / item…" className="h-9 pl-8" />
        </div>
      </div>

      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          isLoading={query.isLoading}
          rowKey={(r) => r.id}
          onRowClick={(r) => setOpenId(r.id)}
          emptyTitle="No job work orders"
          emptyDescription="Create one from a product recipe step to start production."
        />
      )}

      <JobWorkOrderDetailSheet jwoId={openId} open={!!openId} onOpenChange={(o) => !o && setOpenId(null)} />
      <CreateJobWorkOrderSheet
        open={creating}
        onOpenChange={setCreating}
        onCreated={(id) => {
          setCreating(false);
          setOpenId(id);
        }}
      />
    </div>
  );
}

/** Create from a recipe step — pick recipe → step → output qty → warehouse.
 *  Inputs/output/mode/vendor pre-fill from the step BOM server-side. */
function CreateJobWorkOrderSheet({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [recipeId, setRecipeId] = useState('');
  const [stepId, setStepId] = useState('');
  const [outputQty, setOutputQty] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [returnableType, setReturnableType] = useState<'RETURNABLE' | 'NON_RETURNABLE'>('RETURNABLE');

  const recipesQuery = useQuery({
    queryKey: ['production', 'recipes', { status: 'ACTIVE', forJwo: true }],
    queryFn: () => productionService.listRecipes({ status: 'ACTIVE', pageSize: 200 }),
    enabled: open,
    staleTime: 30_000,
  });
  const recipeQuery = useQuery({
    queryKey: ['production', 'recipe', recipeId],
    queryFn: () => productionService.getRecipe(recipeId),
    enabled: open && !!recipeId,
  });
  const warehousesQuery = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => warehousesService.list(),
    enabled: open,
    staleTime: 60_000,
  });

  const recipes = recipesQuery.data?.items ?? [];
  const steps = recipeQuery.data?.steps ?? [];
  const warehouses = warehousesQuery.data ?? [];

  const create = useMutation({
    mutationFn: () => {
      if (!stepId) throw new Error('Pick the step to run.');
      if (!warehouseId) throw new Error('Pick the warehouse.');
      return jobWorkOrdersService.create({
        recipeStepId: stepId,
        outputQty: outputQty.trim() !== '' ? Number(outputQty) : undefined,
        warehouseId,
        returnableType,
      });
    },
    onSuccess: (jwo) => {
      qc.invalidateQueries({ queryKey: ['production', 'job-work-orders'] });
      setRecipeId(''); setStepId(''); setOutputQty(''); setWarehouseId(''); setReturnableType('RETURNABLE');
      onCreated(jwo.id);
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not create'),
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      eyebrow="New job work"
      title="Run a production step"
      description="Pick a recipe step. Its materials and output pre-fill from the recipe; you set the quantity and warehouse."
      submitLabel="Create job work order"
      onSubmit={(e) => { e.preventDefault(); create.mutate(); }}
      isSubmitting={create.isPending}
      size="lg"
    >
      <div className="space-y-3">
        <FormField label="Recipe (finished good)" htmlFor="jwo-recipe" required>
          <Select value={recipeId} onValueChange={(v) => { setRecipeId(v); setStepId(''); }}>
            <SelectTrigger id="jwo-recipe" className="h-9"><SelectValue placeholder="Pick an active recipe" /></SelectTrigger>
            <SelectContent>
              {recipes.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.targetItem.name} — {r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <FormField label="Step" htmlFor="jwo-step" required>
          <Select value={stepId} onValueChange={setStepId} >
            <SelectTrigger id="jwo-step" className="h-9"><SelectValue placeholder={recipeId ? 'Pick a step' : 'Pick a recipe first'} /></SelectTrigger>
            <SelectContent>
              {steps.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.sequence}. {s.name} → {s.outputItem.name}
                  <span className="ml-2 text-[10px] text-muted-foreground">{s.mode === 'OUTSOURCED' ? 'outsourced' : 'in-house'}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Output quantity" htmlFor="jwo-qty" hint="defaults to the step's per-run">
            <Input id="jwo-qty" type="number" min={0} step="0.001" value={outputQty}
              onChange={(e) => setOutputQty(e.target.value)} placeholder="e.g. 50" className="h-9 text-right tabular-nums" />
          </FormField>
          <FormField label="Warehouse" htmlFor="jwo-wh" required>
            <Select value={warehouseId} onValueChange={setWarehouseId}>
              <SelectTrigger id="jwo-wh" className="h-9"><SelectValue placeholder="Pick warehouse" /></SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>

        <FormField
          label="Challan type"
          htmlFor="jwo-returnable"
          hint="Returnable — material comes back after the job; Non-returnable — e.g. scrap, printed at zero value"
        >
          <Select value={returnableType} onValueChange={(v) => setReturnableType(v as 'RETURNABLE' | 'NON_RETURNABLE')}>
            <SelectTrigger id="jwo-returnable" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="RETURNABLE">Returnable</SelectItem>
              <SelectItem value="NON_RETURNABLE">Non-returnable</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
      </div>
    </FormSheet>
  );
}
